/* eslint-disable @typescript-eslint/no-explicit-any */
import type React from 'react'
import { useDrag, useDrop, type DragLayerMonitor, type XYCoord } from 'react-dnd'
import { type Identifier } from 'dnd-core'

interface Props {
  /**
   * Ref for the container element of the item being dropped.
   */
  dropRef: React.MutableRefObject<any>
  /**
   * Optional ref for when the drag source should
   * be different than the drop target element
   */
  dragRef?: React.MutableRefObject<any>
  /**
   * A string to differentiate this type of draggable element from
   * others on a page.
   */
  type: string
  /**
   * Callback for when dragged component at originalIndex is moved to index
   */
  onDrag: (originalIndex: number, index: number) => void
  /**
   * Callback for when dragged item is dropped
   */
  onDrop?: (dropParams: { didDrop: boolean; dropIndex: number }) => void
  /**
   * Index of the current item in this list of drag and drop items.
   */
  index: number
}

interface DragItem {
  index: number
}

/**
 * Hook for incorporating drag & drop reordering behavior into a component.
 */
export const useDragAndDropReorder = (props: Props) => {
  const { type, onDrag, onDrop, index, dropRef, dragRef } = props

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: type,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (!dropRef.current) return

      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = dropRef.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      onDrag(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: type,
    item: (): DragItem => ({ index }),
    collect: (monitor: DragLayerMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: ({ index }, monitor) => onDrop?.({ didDrop: monitor.didDrop(), dropIndex: index }),
  })

  preview(drop(dropRef))
  drag(dragRef ?? dropRef)

  return {
    /**
     * Whether or not the element is being dragged
     */
    isDragging,
    /**
     * To be added to the draggable element's data-handler-id attribute
     * to improve performance.
     */
    handlerId,
  }
}
